import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import {validateIssueBody} from '@github-ui/entity-validators'
import {IssueBodyEditor} from '@github-ui/issue-body/components/IssueBodyEditor'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {clsx} from 'clsx'
import {useMemo, type RefObject} from 'react'
import {useShouldShowCopilotFunctionalityInIssues} from '@github-ui/use-should-show-copilot-functionality-in-issues'
import {Banner} from '@primer/react'
import {Stack} from '@primer/react/experimental'

import {useUpdateDescriptionMutation} from '../../mutations/use-update-description-mutation'
import styles from './DescriptionEditor.module.css'
import {useLayoutBaseRepository, useLayoutPullRequestId, useLayoutPullRequestTitle} from '../../stores/selectors'
import {useCommentBoxSubject} from '@github-ui/diff-lines/use-comment-box-subject'
import {useReturnFocus} from '@github-ui/use-return-focus'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {StaleContentError} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {md2html} from '@github-ui/md2html'
import {announce} from '@github-ui/aria-live'

const PLACEHOLDER_TEXT = 'No description provided.'
const DESCRIPTION_SAVE_ERROR = 'Failed to update description'

/** Session storage key for persisting an unsaved PR description draft. */
const prDescriptionDraftKey = (pullRequestId: number) => `pr-description-draft-${pullRequestId}`

type DescriptionEditorProps = {
  /** Current description as sanitized HTML, or null/undefined if none. Used by the read-only viewer. */
  bodyHtml: SafeHTMLString | null | undefined
  /** Current description as raw markdown. Used as the initial draft when editing. */
  body: string
  isEditing: boolean
  onEditingChange: (isEditing: boolean) => void
  /** Class for the viewer container; ignored when editing. */
  viewerClassName?: string
  /** Element to receive focus when the editor is dismissed (e.g. the edit trigger). */
  returnFocusRef?: RefObject<HTMLElement | null>
}

/**
 * Shared description display + editing experience for pull requests. Handles
 * toggling between a `NewMarkdownViewer` and a `CommentBox` editor, draft
 * persistence, stale-content detection, and saving via `useUpdateDescriptionMutation`.
 *
 * The parent owns the `isEditing` state so it can place the edit trigger
 * wherever it makes sense (e.g. an overview kebab menu, a panel title bar).
 * While editing, the trigger should remain mounted but marked `inactive`.
 */
export function DescriptionEditor({
  bodyHtml,
  body,
  isEditing,
  onEditingChange,
  viewerClassName,
  returnFocusRef,
}: DescriptionEditorProps) {
  const pullRequestId = useLayoutPullRequestId()
  const pullRequestTitle = useLayoutPullRequestTitle()
  const baseRepository = useLayoutBaseRepository()

  const mutation = useUpdateDescriptionMutation()

  const optimisticBody = mutation.isPending ? mutation.variables.body : body
  const optimisticBodyHtml = mutation.isPending ? md2html(optimisticBody) : (bodyHtml ?? '')
  const isEmptyDescription = optimisticBodyHtml === ''

  const [draft, setDraft, clearDraft] = useLocalStorage<string | undefined>(
    prDescriptionDraftKey(pullRequestId),
    undefined,
  )

  const editorValue = draft ?? body

  const repoVisibility = baseRepository.public ? 'PUBLIC' : 'PRIVATE'
  const shouldShowCopilot = useShouldShowCopilotFunctionalityInIssues({
    repositoryVisibility: repoVisibility,
    userCanPushToRepo: baseRepository.currentUserCanPush ?? false,
  })

  const closeEditor = () => {
    onEditingChange(false)
    if (returnFocusRef) {
      requestAnimationFrame(() => returnFocusRef.current?.focus())
    }
  }

  const subject = useCommentBoxSubject({surface: 'description'})

  const validationResult = useMemo(() => validateIssueBody(editorValue), [editorValue])

  const handleSave = () => {
    if (mutation.isPending || !validationResult.isValid) return

    mutation.mutate(
      {body: editorValue},
      {
        onSuccess: () => {
          announce('Pull request description updated successfully.')
          clearDraft()
        },
        // revert back to edit mode on error
        onError: () => onEditingChange(true),
      },
    )

    // Optimistically revert to read mode
    closeEditor()
  }

  // This is intentionally a separate mutation instance from the main edit functionality, so that it doesn't share in
  // the optimistic update logic (which looks janky when interacting with checkboxes because the client side Markdown
  // renderer md2html doesn't support checkboxes).
  const directEditMutation = useUpdateDescriptionMutation()

  /** Called when Markdown is updated without entering edit mode (ie, checkbox interaction). */
  const handleMarkdownChange = async (newValue: string) => {
    try {
      await directEditMutation.mutateAsync({body: newValue})
    } catch {
      /* noop: TSQ will handle the error in addition to rejecting the promise */
    }
  }

  const bodyIsStale = mutation.error instanceof StaleContentError

  const onCancel = () => {
    if (!bodyIsStale) clearDraft()
    mutation.reset()
    closeEditor()
  }

  const isPending = mutation.isPending || directEditMutation.isPending

  const {returnFocusRef: errorBannerReturnFocusRef, containerRef: errorBannerRef} = useReturnFocus()
  const {returnFocusRef: directErrorBannerReturnFocusRef, containerRef: directErrorBannerRef} = useReturnFocus()

  if (isEditing) {
    return (
      <Stack direction="vertical" gap="condensed">
        {!bodyIsStale && mutation.error && (
          <Banner
            role="alert"
            variant="critical"
            title={DESCRIPTION_SAVE_ERROR}
            onDismiss={mutation.reset}
            ref={errorBannerRef}
          >
            {mutation.error.message}
          </Banner>
        )}
        <IssueBodyEditor
          editorDisabled={mutation.isPending}
          subjectId={pullRequestId.toString()}
          subject={subject}
          body={editorValue}
          bodyIsStale={bodyIsStale}
          onChange={setDraft}
          onCancel={onCancel}
          onCommit={handleSave}
          repoOwner={baseRepository.ownerLogin}
          repoName={baseRepository.name}
          title={pullRequestTitle}
          shouldShowCopilotFunctionality={shouldShowCopilot}
          trailingIcon={false}
          ref={errorBannerReturnFocusRef}
        />
      </Stack>
    )
  }

  return (
    <div className={clsx(viewerClassName, isPending && styles.pendingBody)}>
      {isEmptyDescription ? (
        <div className="fgColor-muted text-italic">{PLACEHOLDER_TEXT}</div>
      ) : (
        <Stack direction="vertical" gap="condensed" tabIndex={-1} ref={directErrorBannerReturnFocusRef}>
          <NewMarkdownViewer
            verifiedHTML={optimisticBodyHtml}
            disabled={isPending}
            markdownValue={optimisticBody}
            onChange={handleMarkdownChange}
          />
          {directEditMutation.error && (
            // The error banner can appear in read mode when markdown updates (checkboxes) fail to save
            <Banner
              role="alert"
              variant="critical"
              title={DESCRIPTION_SAVE_ERROR}
              onDismiss={directEditMutation.reset}
              ref={directErrorBannerRef}
            >
              {directEditMutation.error.message}
            </Banner>
          )}
        </Stack>
      )}
    </div>
  )
}
